import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Checkbox,
  Spin,
  Input,
  message,
  Divider,
  InputNumber,
  Space,
  Select,
  Tooltip,
  Modal,
  Upload,
} from 'antd';
import { ArrowRightOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons';
import DataTable from '../components/DataTable';
import { getPreview, getColumns, uploadFile } from '../services/api';
import type { ColumnInfo, PreviewData } from '../types';

const MISSING_STRATEGIES = [
  { value: 'none', label: '不处理' },
  { value: 'drop', label: '删除含缺失值的行' },
  { value: 'mean', label: '用均值填充' },
  { value: 'median', label: '用中位数填充' },
];

const AnalysisDataSelect: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileId = searchParams.get('fileId') || '';

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [selectedCols, setSelectedCols] = useState<string[]>([]);
  const [usl, setUsl] = useState<number | null>(null);
  const [lsl, setLsl] = useState<number | null>(null);
  const [chatInput, setChatInput] = useState('');

  // Task 6: Missing value handling
  const [missingStrategy, setMissingStrategy] = useState<string>('none');

  // Task 3: Re-upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prevData, colData] = await Promise.all([
          getPreview(fileId),
          getColumns(fileId),
        ]);
        setPreview(prevData);
        setColumns(colData);
        if (colData.length > 0) {
          setSelectedCols(colData.filter((c) => c.dtype === 'numeric' || c.dtype === 'float' || c.dtype === 'integer').slice(0, 3).map((c) => c.name));
        }
      } catch {
        message.error('加载数据失败，请确认后端服务已启动');
      } finally {
        setLoading(false);
      }
    };
    if (fileId) fetchData();
  }, [fileId]);

  // Task 1: Select All helpers
  const allColNames = useMemo(() => columns.map((c) => c.name), [columns]);
  const isAllSelected = selectedCols.length === allColNames.length && allColNames.length > 0;
  const isIndeterminate = selectedCols.length > 0 && selectedCols.length < allColNames.length;

  const handleSelectAll = (checked: boolean) => {
    setSelectedCols(checked ? [...allColNames] : []);
  };

  // Task 6: Missing value stats
  const missingStats = useMemo(() => {
    if (!columns.length || !preview) return [];
    return columns.map((col) => {
      const total = preview.total_rows || preview.data.length;
      const missingCount = col.missing_rate !== undefined ? Math.round(col.missing_rate * total) : 0;
      const pct = col.missing_rate !== undefined ? (col.missing_rate * 100).toFixed(1) : '0.0';
      return { name: col.name, missing: missingCount, pct, total };
    });
  }, [columns, preview]);

  // Task 3: Re-upload handler
  const handleReUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await uploadFile(file);
      setUploadModalOpen(false);
      message.success('文件上传成功，正在加载新数据...');
      setSearchParams({ fileId: result.file_id });
    } catch {
      message.error('上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleNext = () => {
    if (selectedCols.length === 0) {
      message.warning('请至少选择一列数据');
      return;
    }
    const params = encodeURIComponent(
      JSON.stringify({
        fileId,
        columns: selectedCols,
        usl: usl,
        lsl: lsl,
        missingStrategy: missingStrategy !== 'none' ? missingStrategy : undefined,
      })
    );
    navigate(`/analysis/type-select?params=${params}`);
  };

  const handleChat = () => {
    if (!chatInput.trim()) return;
    const input = chatInput.trim().toLowerCase();
    const colNames = columns.map((c) => c.name);
    let matched = false;

    const selectKeywords = ['选', '用', '添加', '加上', '使用', '分析'];
    const removeKeywords = ['去掉', '移除', '删除', '取消', '不要', '不用'];

    for (const colName of colNames) {
      const colLower = colName.toLowerCase();
      if (!input.includes(colLower)) continue;

      if (removeKeywords.some((kw) => input.includes(kw))) {
        setSelectedCols((prev) => prev.filter((c) => c !== colName));
        message.success(`已取消选中「${colName}」列`);
        matched = true;
      } else if (selectKeywords.some((kw) => input.includes(kw)) || input.includes(colLower)) {
        if (!selectedCols.includes(colName)) {
          setSelectedCols((prev) => [...prev, colName]);
        }
        message.success(`已选中「${colName}」列`);
        matched = true;
      }

      if (matched) break;
    }

    // Check for USL/LSL keywords
    if (!matched) {
      const uslMatch = input.match(/(?:usl|规格上限|上限)[^\d]*(\d+(?:\.\d+)?)/);
      const lslMatch = input.match(/(?:lsl|规格下限|下限)[^\d]*(\d+(?:\.\d+)?)/);
      if (uslMatch) {
        setUsl(parseFloat(uslMatch[1]));
        message.success(`已设置规格上限 USL = ${uslMatch[1]}`);
        matched = true;
      }
      if (lslMatch) {
        setLsl(parseFloat(lslMatch[1]));
        message.success(`已设置规格下限 LSL = ${lslMatch[1]}`);
        matched = true;
      }
    }

    if (!matched) {
      message.info(`未能识别列名，当前可用列：${colNames.join('、')}`);
    }

    setChatInput('');
  };

  const columnTypes: Record<string, string> = {};
  columns.forEach((c) => {
    columnTypes[c.name] = c.dtype;
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" tip="正在加载数据预览..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 24 }}>
      <div style={{ flex: 1 }}>
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#061b31', fontWeight: 600 }}>数据预览</span>
              {/* Task 3: Re-upload button */}
              <Tooltip title="重新上传文件">
                <Button
                  icon={<UploadOutlined />}
                  size="small"
                  type="text"
                  style={{ color: '#7c4dff' }}
                  onClick={() => setUploadModalOpen(true)}
                />
              </Tooltip>
            </div>
          }
          style={{ marginBottom: 16 }}
        >
          {preview && (
            <DataTable
              columns={preview.columns}
              data={preview.data}
              totalRows={preview.total_rows}
              columnTypes={columnTypes}
              selectable
              selectedColumns={selectedCols}
              onColumnSelect={setSelectedCols}
              sortable
            />
          )}
        </Card>
      </div>

      <div style={{ width: 320, flexShrink: 0 }}>
        <Card
          title={<span style={{ color: '#061b31', fontWeight: 600 }}>数据选择</span>}
          style={{ marginBottom: 16 }}
        >
          {/* Task 1: Select All / Deselect All */}
          <Divider orientation="left" style={{ fontSize: 13, margin: '0 0 12px' }}>
            分析列
          </Divider>
          <div style={{ marginBottom: 8 }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={(e) => handleSelectAll(e.target.checked)}
              style={{ fontSize: 13, fontWeight: 500 }}
            >
              全选 / 取消全选
            </Checkbox>
          </div>
          <Checkbox.Group
            value={selectedCols}
            onChange={(vals) => setSelectedCols(vals as string[])}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {columns.map((col) => (
              <Checkbox key={col.name} value={col.name}>
                <span style={{ fontSize: 13 }}>{col.name}</span>
                <span style={{ fontSize: 11, color: '#999', marginLeft: 6 }}>({col.dtype})</span>
              </Checkbox>
            ))}
          </Checkbox.Group>

          <Divider orientation="left" style={{ fontSize: 13, margin: '16px 0 12px' }}>
            数据选项
          </Divider>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>规格上限 (USL):</div>
              <InputNumber
                value={usl}
                onChange={(v) => setUsl(v)}
                placeholder="选填"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>规格下限 (LSL):</div>
              <InputNumber
                value={lsl}
                onChange={(v) => setLsl(v)}
                placeholder="选填"
                style={{ width: '100%' }}
              />
            </div>
          </Space>

          {/* Task 6: Missing value handling */}
          <Divider orientation="left" style={{ fontSize: 13, margin: '16px 0 12px' }}>
            缺失值处理
          </Divider>
          <div style={{ marginBottom: 8 }}>
            {missingStats.filter((s) => s.missing > 0).length === 0 ? (
              <div style={{ fontSize: 12, color: '#52c41a' }}>数据无缺失值</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
                {missingStats
                  .filter((s) => s.missing > 0)
                  .map((s) => (
                    <div key={s.name} style={{ fontSize: 12, color: '#fa8c16' }}>
                      {s.name}: {s.missing} 缺失 ({s.pct}%)
                    </div>
                  ))}
              </div>
            )}
            <Select
              value={missingStrategy}
              onChange={setMissingStrategy}
              options={MISSING_STRATEGIES}
              style={{ width: '100%' }}
              size="small"
            />
          </div>
        </Card>

        <Button
          type="primary"
          icon={<ArrowRightOutlined />}
          size="large"
          block
          onClick={handleNext}
        >
          下一步：选择分析类型
        </Button>
      </div>

      {/* Task 3: Re-upload Modal */}
      <Modal
        title="重新上传文件"
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        footer={null}
        width={480}
      >
        <Upload.Dragger
          accept=".csv,.xlsx,.xls,.tsv"
          showUploadList={false}
          beforeUpload={(file) => {
            handleReUpload(file);
            return false;
          }}
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: 48, color: '#7c4dff' }} />
          </p>
          <p className="ant-upload-text">
            {uploading ? '正在上传...' : '点击或拖拽文件到此区域上传'}
          </p>
          <p className="ant-upload-hint">支持 CSV、Excel 格式文件</p>
        </Upload.Dragger>
      </Modal>

      <div
        style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '60%',
          maxWidth: 700,
          background: '#fff',
          borderRadius: 10,
          padding: '12px 16px',
          display: 'flex',
          gap: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 50,
        }}
      >
        <Input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="描述您的分析需求，例如：我想检验两组数据是否有显著差异"
          onPressEnter={handleChat}
          size="large"
        />
        <Button type="primary" icon={<SendOutlined />} size="large" onClick={handleChat}>
          发送
        </Button>
      </div>
    </div>
  );
};

export default AnalysisDataSelect;
